/**
 * Coop-admin — Join requests queue.
 *
 * Coop-admin (or admin) lands here from the sidebar and sees every pending
 * request from a farmer asking to join a cooperative they administer. Each
 * card shows the farmer's name + phone + target coop, plus [Approve] /
 * [Reject] buttons that PATCH `/coop/join-requests/:id` with the decision.
 *
 * On success, the row disappears from the list (the list query is
 * invalidated) and the farmers query is invalidated too so a newly-approved
 * member shows up on the roster without a manual refresh; a Snackbar confirms
 * the decision.
 *
 * Mirrors `_app.pending-reviews.tsx` (the farmer-profile-edit review queue).
 * The destructive Reject step now uses an @fse/ui `Dialog` confirm + a
 * `Snackbar` toast (TD-085) instead of the previous inline two-button state.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Snackbar,
  Stack,
  Chip,
  Text,
} from '@fse/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getCooperativesControllerListJoinRequestsQueryKey,
  useCooperativesControllerDecideJoinRequest,
  useCooperativesControllerListJoinRequests,
} from '../api/generated/cooperatives/cooperatives';
import type {
  JoinRequestDecisionDtoDecision as JoinRequestDecision,
  JoinRequestDto,
} from '../api/generated/models';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function JoinRequestsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const listQuery = useCooperativesControllerListJoinRequests();
  const [toast, setToast] = useState<string | null>(null);

  function onDecided(decision: JoinRequestDecision) {
    queryClient.invalidateQueries({ queryKey: getCooperativesControllerListJoinRequestsQueryKey() });
    // An approval adds a farmer to the coop — refresh the roster so the new
    // member appears there without a manual reload.
    queryClient.invalidateQueries({ queryKey: ['farmers'] });
    setToast(
      decision === 'approve' ? t('joinRequests.toast.approved') : t('joinRequests.toast.rejected'),
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('joinRequests.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('joinRequests.subtitle')}
          </Text>
        </Box>
      </Box>

      {listQuery.isPending ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      ) : listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              {t('joinRequests.retry')}
            </Button>
          }
        >
          {t('joinRequests.loadError', { error: String(listQuery.error) })}
        </Alert>
      ) : listQuery.data.length === 0 ? (
        <Card>
          <CardHeader title={t('joinRequests.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('joinRequests.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {listQuery.data.map((req) => (
            <JoinRequestCard key={req.id} request={req} onDecided={onDecided} />
          ))}
        </Stack>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

interface JoinRequestCardProps {
  request: JoinRequestDto;
  onDecided: (decision: JoinRequestDecision) => void;
}

function JoinRequestCard({ request, onDecided }: JoinRequestCardProps) {
  const { t } = useTranslation();
  // Destructive "Reject" goes through a confirm Dialog (TD-085); Approve is
  // one-click.
  const [rejectOpen, setRejectOpen] = useState(false);

  const decideMutation = useCooperativesControllerDecideJoinRequest({
    mutation: {
      onSuccess: (_result, variables) => {
        setRejectOpen(false);
        onDecided(variables.data.decision);
      },
    },
  });

  const submit = (decision: JoinRequestDecision) => {
    decideMutation.mutate({ id: request.id, data: { decision } });
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Text variant="title-medium" as="span">
              {request.displayName}
            </Text>
            <Chip kind="input" color="neutral" label={request.phoneE164} />
            <Chip kind="assist" color="success" label={request.cooperativeName} />
          </Stack>
        }
        subheader={
          <Text variant="label-medium" color="text.secondary">
            {t('joinRequests.card.requested', {
              date: dateFormatter.format(new Date(request.requestedAt)),
            })}
          </Text>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Text variant="body-medium" color="text.secondary">
            {t('joinRequests.card.explainer', { coop: request.cooperativeName })}
          </Text>

          {/* Approve errors surface here; reject errors surface inside the dialog. */}
          {decideMutation.isError && !rejectOpen ? (
            <Alert severity="error">
              {t('joinRequests.card.decisionError', { error: String(decideMutation.error) })}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              disabled={decideMutation.isPending}
              onClick={() => setRejectOpen(true)}
            >
              {t('joinRequests.card.reject')}
            </Button>
            <Button
              variant="contained"
              disabled={decideMutation.isPending}
              onClick={() => submit('approve')}
            >
              {decideMutation.isPending && !rejectOpen
                ? t('joinRequests.card.saving')
                : t('joinRequests.card.approve')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <Dialog
        open={rejectOpen}
        onClose={() => {
          if (!decideMutation.isPending) setRejectOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('joinRequests.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('joinRequests.dialog.body', {
              name: request.displayName,
              coop: request.cooperativeName,
            })}
          </DialogContentText>
          {decideMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(decideMutation.error)}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            disabled={decideMutation.isPending}
            onClick={() => setRejectOpen(false)}
          >
            {t('joinRequests.dialog.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={decideMutation.isPending}
            onClick={() => submit('reject')}
          >
            {decideMutation.isPending
              ? t('joinRequests.dialog.rejecting')
              : t('joinRequests.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
